import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Boxes,
  ClipboardList,
  Download,
  PackagePlus,
  Plus,
  RefreshCw,
  Search,
  Truck,
} from 'lucide-react';
import { GlassButton, GlassCard, GlassInput, GlassSelect, GlassTextarea, FadeIn } from '../../components/ui';
import { api } from '../../hooks/useApi';

const emptyMaterial = {
  codigo: '',
  nome: '',
  categoria: 'obra',
  unidade: 'un',
  estoque_minimo: '',
  estoque_atual: '',
  custo_unitario: '',
  fornecedor_id: '',
};

const emptyFornecedor = {
  nome: '',
  tipo: 'ambos',
  cnpj: '',
  telefone: '',
  email: '',
  contato: '',
};

const emptyMovimento = {
  tipo: 'entrada',
  material_id: '',
  quantidade: '',
  valor_unitario: '',
  motivo: 'ajuste',
  obra_id: '',
  etapa_id: '',
  fornecedor_id: '',
  documento: '',
  observacoes: '',
};

const emptyEquipamento = {
  codigo: '',
  nome: '',
  categoria: 'ferramenta',
  marca: '',
  modelo: '',
  numero_serie: '',
  patrimonio: '',
  status: 'disponivel',
  condicao: 'bom',
  localizacao: 'almoxarifado',
  obra_id: '',
  responsavel_nome: '',
  data_aquisicao: '',
  valor_aquisicao: '',
  data_garantia: '',
  proxima_manutencao: '',
  observacoes: '',
};

const emptyEquipMov = {
  tipo: 'alocacao',
  data: new Date().toISOString().slice(0, 10),
  obra_id: '',
  localizacao: '',
  responsavel_nome: '',
  condicao: 'bom',
  custo: '',
  proxima_manutencao: '',
  observacao: '',
};

const tabs = [
  { id: 'materiais', label: 'Materiais' },
  { id: 'pedidos', label: 'Pedidos' },
  { id: 'movimentos', label: 'Movimentos' },
  { id: 'equipamentos', label: 'Equipamentos' },
  { id: 'fornecedores', label: 'Fornecedores' },
];

const fmtBRL = (value) => Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtNum = (value) => Number(value || 0).toLocaleString('pt-BR', { maximumFractionDigits: 3 });
const fmtDate = (value) => value ? new Date(value).toLocaleDateString('pt-BR') : '-';

export const Estoque = () => {
  const [activeTab, setActiveTab] = useState('materiais');
  const [dashboard, setDashboard] = useState({});
  const [materiais, setMateriais] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [pedidos, setPedidos] = useState([]);
  const [movimentos, setMovimentos] = useState([]);
  const [equipamentos, setEquipamentos] = useState([]);
  const [obras, setObras] = useState([]);
  const [etapas, setEtapas] = useState([]);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [materialForm, setMaterialForm] = useState(emptyMaterial);
  const [fornecedorForm, setFornecedorForm] = useState(emptyFornecedor);
  const [movimentoForm, setMovimentoForm] = useState(emptyMovimento);
  const [equipamentoForm, setEquipamentoForm] = useState(emptyEquipamento);
  const [equipMovForm, setEquipMovForm] = useState(emptyEquipMov);
  const [selectedEquipamentoId, setSelectedEquipamentoId] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [dashRes, matRes, fornRes, pedRes, movRes, obrasRes, equipRes] = await Promise.all([
        api.get('/estoque/dashboard'),
        api.get('/estoque/materiais', { params: { per_page: 300, search } }),
        api.get('/estoque/fornecedores'),
        api.get('/estoque/pedidos'),
        api.get('/estoque/movimentacoes'),
        api.get('/estoque/obras'),
        api.get('/estoque/equipamentos', { params: { search } }),
      ]);
      setDashboard(dashRes.data || {});
      setMateriais(matRes.data.items || []);
      setFornecedores(fornRes.data.items || []);
      setPedidos(pedRes.data.items || []);
      setMovimentos(movRes.data.items || []);
      setObras(obrasRes.data.items || []);
      setEquipamentos(equipRes.data.items || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao carregar estoque.');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { loadData(); }, [loadData]);

  const materialOptions = useMemo(() => materiais.map((item) => ({ value: item.id, label: `${item.nome} (${item.unidade})` })), [materiais]);
  const fornecedorOptions = useMemo(() => fornecedores.map((item) => ({ value: item.id, label: item.nome })), [fornecedores]);
  const obraOptions = useMemo(() => obras.map((item) => ({ value: item.id, label: item.nome || item.codigo })), [obras]);
  const equipamentoOptions = useMemo(() => equipamentos.map((item) => ({ value: item.id, label: item.codigo ? `${item.codigo} - ${item.nome}` : item.nome })), [equipamentos]);
  const pendingPedidos = pedidos.filter((pedido) => pedido.status !== 'recebido');

  const closeModal = () => {
    setModal('');
    setError('');
  };

  const loadEtapas = async (obraId) => {
    if (!obraId) {
      setEtapas([]);
      return;
    }
    try {
      const res = await api.get(`/estoque/obras/${obraId}/etapas`);
      setEtapas(res.data.items || []);
    } catch (err) {
      setEtapas([]);
    }
  };

  const saveMaterial = async () => {
    if (!materialForm.nome) {
      setError('Nome do material e obrigatorio.');
      return;
    }
    setSaving(true);
    try {
      await api.post('/estoque/materiais', materialForm);
      setMaterialForm(emptyMaterial);
      closeModal();
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao salvar material.');
    } finally {
      setSaving(false);
    }
  };

  const saveFornecedor = async () => {
    if (!fornecedorForm.nome) {
      setError('Nome do fornecedor e obrigatorio.');
      return;
    }
    setSaving(true);
    try {
      await api.post('/estoque/fornecedores', fornecedorForm);
      setFornecedorForm(emptyFornecedor);
      closeModal();
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao salvar fornecedor.');
    } finally {
      setSaving(false);
    }
  };

  const saveMovimento = async () => {
    if (!movimentoForm.material_id || !movimentoForm.quantidade) {
      setError('Material e quantidade sao obrigatorios.');
      return;
    }
    setSaving(true);
    try {
      await api.post('/estoque/movimentacoes', movimentoForm);
      setMovimentoForm(emptyMovimento);
      setEtapas([]);
      closeModal();
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao salvar movimento.');
    } finally {
      setSaving(false);
    }
  };

  const receberPedido = async (pedidoId) => {
    setSaving(true);
    try {
      await api.post(`/estoque/pedidos/${pedidoId}/receber`, {});
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao receber pedido.');
    } finally {
      setSaving(false);
    }
  };

  const saveEquipamento = async () => {
    if (!equipamentoForm.nome) {
      setError('Nome do equipamento e obrigatorio.');
      return;
    }
    setSaving(true);
    try {
      await api.post('/estoque/equipamentos', equipamentoForm);
      setEquipamentoForm(emptyEquipamento);
      closeModal();
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao salvar equipamento.');
    } finally {
      setSaving(false);
    }
  };

  const saveEquipMov = async () => {
    if (!selectedEquipamentoId) {
      setError('Selecione um equipamento.');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/estoque/equipamentos/${selectedEquipamentoId}/movimentacoes`, equipMovForm);
      setEquipMovForm(emptyEquipMov);
      setSelectedEquipamentoId('');
      closeModal();
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao movimentar equipamento.');
    } finally {
      setSaving(false);
    }
  };

  const openMovimento = (tipo = 'entrada') => {
    setMovimentoForm({ ...emptyMovimento, tipo, motivo: tipo === 'saida' ? 'consumo' : 'ajuste' });
    setModal('movimento');
  };

  const statCards = [
    { label: 'Materiais', value: dashboard.materiais || 0, icon: Boxes },
    { label: 'Baixo estoque', value: dashboard.baixo_estoque || 0, icon: AlertTriangle },
    { label: 'Pedidos pendentes', value: dashboard.pedidos_pendentes || 0, icon: ClipboardList },
    { label: 'Valor em estoque', value: fmtBRL(dashboard.valor_estoque), icon: PackagePlus },
    { label: 'Equipamentos', value: dashboard.equipamentos || 0, icon: Truck },
  ];

  return (
    <FadeIn>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white">Estoque e Compras</h1>
            <p className="text-white/60 mt-1">Materiais, fornecedores, recebimentos e consumo operacional.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <GlassButton onClick={() => window.open('/erp/manuals/playbook-humano-estoque-compras.md', '_blank')} variant="secondary">
              <Download className="w-4 h-4" /> Manual
            </GlassButton>
            <GlassButton onClick={loadData} variant="secondary" disabled={loading}>
              <RefreshCw className="w-4 h-4" /> Atualizar
            </GlassButton>
            <GlassButton onClick={() => setModal('material')}>
              <Plus className="w-4 h-4" /> Material
            </GlassButton>
            <GlassButton onClick={() => setModal('equipamento')} variant="secondary">
              <Truck className="w-4 h-4" /> Equipamento
            </GlassButton>
            <GlassButton onClick={() => openMovimento('saida')}>
              <PackagePlus className="w-4 h-4" /> Consumo
            </GlassButton>
          </div>
        </div>

        {error && <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">{error}</div>}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {statCards.map((card) => (
            <GlassCard key={card.label} className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/50">{card.label}</p>
                  <p className="text-2xl font-semibold text-white mt-1">{card.value}</p>
                </div>
                <card.icon className="w-8 h-8 text-emerald-300" />
              </div>
            </GlassCard>
          ))}
        </div>

        <GlassCard className="p-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === tab.id ? 'bg-white/15 text-white' : 'text-white/60 hover:bg-white/5 hover:text-white'}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Search className="w-4 h-4 text-white/40" />
              <GlassInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar material" />
            </div>
          </div>
        </GlassCard>

        {activeTab === 'materiais' && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {materiais.map((material) => (
              <GlassCard key={material.id} className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-white">{material.nome}</h3>
                    <p className="text-sm text-white/50">{material.codigo || 'Sem codigo'} · {material.categoria || 'geral'} · {material.unidade}</p>
                  </div>
                  {material.baixo_estoque && <span className="rounded-full bg-amber-500/20 px-3 py-1 text-xs text-amber-200">baixo estoque</span>}
                </div>
                <div className="grid grid-cols-3 gap-3 mt-4 text-sm">
                  <Info label="Atual" value={fmtNum(material.estoque_atual)} />
                  <Info label="Minimo" value={fmtNum(material.estoque_minimo)} />
                  <Info label="Custo" value={fmtBRL(material.custo_unitario)} />
                </div>
                <p className="mt-3 text-sm text-white/50">Fornecedor: {material.fornecedor || '-'}</p>
              </GlassCard>
            ))}
          </div>
        )}

        {activeTab === 'pedidos' && (
          <GlassCard className="overflow-hidden">
            <TableHeader cols={['Pedido', 'Fornecedor', 'Obra', 'Valor', 'Status', 'Acoes']} />
            {pedidos.map((pedido) => (
              <div key={pedido.id} className="grid grid-cols-6 gap-4 px-5 py-4 border-t border-white/10 text-sm text-white/80">
                <span>{pedido.numero || pedido.id.slice(0, 8)}</span>
                <span>{pedido.fornecedor || '-'}</span>
                <span>{pedido.obra || '-'}</span>
                <span>{fmtBRL(pedido.valor_total)}</span>
                <span>{pedido.status}</span>
                <span>
                  {pedido.status !== 'recebido' && (
                    <button className="text-emerald-300 hover:text-emerald-200" disabled={saving} onClick={() => receberPedido(pedido.id)}>
                      receber
                    </button>
                  )}
                </span>
              </div>
            ))}
            {pendingPedidos.length === 0 && <Empty text="Nenhum pedido pendente para recebimento." />}
          </GlassCard>
        )}

        {activeTab === 'movimentos' && (
          <GlassCard className="overflow-hidden">
            <div className="flex justify-end gap-2 p-4">
              <GlassButton variant="secondary" onClick={() => openMovimento('entrada')}><Truck className="w-4 h-4" /> Entrada</GlassButton>
              <GlassButton onClick={() => openMovimento('saida')}><PackagePlus className="w-4 h-4" /> Saida/Consumo</GlassButton>
            </div>
            <TableHeader cols={['Data', 'Material', 'Tipo', 'Qtd', 'Obra', 'Documento']} />
            {movimentos.map((mov) => (
              <div key={mov.id} className="grid grid-cols-6 gap-4 px-5 py-4 border-t border-white/10 text-sm text-white/80">
                <span>{fmtDate(mov.created_at)}</span>
                <span>{mov.material || '-'}</span>
                <span>{mov.tipo}</span>
                <span>{fmtNum(mov.quantidade)}</span>
                <span>{mov.obra || '-'}</span>
                <span>{mov.documento || '-'}</span>
              </div>
            ))}
          </GlassCard>
        )}

        {activeTab === 'equipamentos' && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <GlassButton onClick={() => setModal('equipamento')}><Plus className="w-4 h-4" /> Equipamento</GlassButton>
              <GlassButton variant="secondary" onClick={() => setModal('equipMov')}><Truck className="w-4 h-4" /> Movimentar</GlassButton>
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {equipamentos.map((equipamento) => (
                <GlassCard key={equipamento.id} className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-semibold text-white">{equipamento.nome}</h3>
                      <p className="text-sm text-white/50">{equipamento.codigo || equipamento.patrimonio || 'Sem codigo'} · {equipamento.categoria || 'equipamento'}</p>
                    </div>
                    <span className="rounded-full bg-white/10 px-3 py-1 text-xs text-white/70">{equipamento.status}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-3 mt-4 text-sm">
                    <Info label="Condicao" value={equipamento.condicao || '-'} />
                    <Info label="Obra" value={equipamento.obra || '-'} />
                    <Info label="Responsavel" value={equipamento.responsavel_nome || '-'} />
                  </div>
                  <p className="mt-3 text-sm text-white/50">Localizacao: {equipamento.localizacao || '-'}</p>
                  <p className="mt-1 text-sm text-white/50">Prox. manutencao: {fmtDate(equipamento.proxima_manutencao)}</p>
                </GlassCard>
              ))}
              {equipamentos.length === 0 && <GlassCard className="p-8 text-center text-white/50">Nenhum equipamento cadastrado.</GlassCard>}
            </div>
          </div>
        )}

        {activeTab === 'fornecedores' && (
          <div className="space-y-3">
            <GlassButton onClick={() => setModal('fornecedor')}><Plus className="w-4 h-4" /> Fornecedor</GlassButton>
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
              {fornecedores.map((fornecedor) => (
                <GlassCard key={fornecedor.id} className="p-5">
                  <h3 className="text-lg font-semibold text-white">{fornecedor.nome}</h3>
                  <p className="text-sm text-white/50">{fornecedor.tipo || 'ambos'} · {fornecedor.cnpj || 'sem CNPJ'}</p>
                  <p className="text-sm text-white/60 mt-3">{fornecedor.contato || fornecedor.email || fornecedor.telefone || '-'}</p>
                </GlassCard>
              ))}
            </div>
          </div>
        )}

        {modal === 'material' && (
          <Modal title="Novo material" onClose={closeModal}>
            <FormGrid>
              <GlassInput placeholder="Nome" value={materialForm.nome} onChange={(e) => setMaterialForm({ ...materialForm, nome: e.target.value })} />
              <GlassInput placeholder="Codigo" value={materialForm.codigo} onChange={(e) => setMaterialForm({ ...materialForm, codigo: e.target.value })} />
              <GlassInput placeholder="Categoria" value={materialForm.categoria} onChange={(e) => setMaterialForm({ ...materialForm, categoria: e.target.value })} />
              <GlassInput placeholder="Unidade" value={materialForm.unidade} onChange={(e) => setMaterialForm({ ...materialForm, unidade: e.target.value })} />
              <GlassInput placeholder="Estoque minimo" value={materialForm.estoque_minimo} onChange={(e) => setMaterialForm({ ...materialForm, estoque_minimo: e.target.value })} />
              <GlassInput placeholder="Estoque atual" value={materialForm.estoque_atual} onChange={(e) => setMaterialForm({ ...materialForm, estoque_atual: e.target.value })} />
              <GlassInput placeholder="Custo unitario" value={materialForm.custo_unitario} onChange={(e) => setMaterialForm({ ...materialForm, custo_unitario: e.target.value })} />
              <GlassSelect value={materialForm.fornecedor_id} onChange={(e) => setMaterialForm({ ...materialForm, fornecedor_id: e.target.value })}>
                <option value="">Fornecedor padrao</option>
                {fornecedorOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
              </GlassSelect>
            </FormGrid>
            <ModalActions onCancel={closeModal} onSave={saveMaterial} saving={saving} />
          </Modal>
        )}

        {modal === 'fornecedor' && (
          <Modal title="Novo fornecedor" onClose={closeModal}>
            <FormGrid>
              <GlassInput placeholder="Nome" value={fornecedorForm.nome} onChange={(e) => setFornecedorForm({ ...fornecedorForm, nome: e.target.value })} />
              <GlassSelect value={fornecedorForm.tipo} onChange={(e) => setFornecedorForm({ ...fornecedorForm, tipo: e.target.value })}>
                <option value="material">Material</option>
                <option value="servico">Servico</option>
                <option value="ambos">Ambos</option>
              </GlassSelect>
              <GlassInput placeholder="CNPJ" value={fornecedorForm.cnpj} onChange={(e) => setFornecedorForm({ ...fornecedorForm, cnpj: e.target.value })} />
              <GlassInput placeholder="Telefone" value={fornecedorForm.telefone} onChange={(e) => setFornecedorForm({ ...fornecedorForm, telefone: e.target.value })} />
              <GlassInput placeholder="Email" value={fornecedorForm.email} onChange={(e) => setFornecedorForm({ ...fornecedorForm, email: e.target.value })} />
              <GlassInput placeholder="Contato" value={fornecedorForm.contato} onChange={(e) => setFornecedorForm({ ...fornecedorForm, contato: e.target.value })} />
            </FormGrid>
            <ModalActions onCancel={closeModal} onSave={saveFornecedor} saving={saving} />
          </Modal>
        )}

        {modal === 'equipamento' && (
          <Modal title="Novo equipamento" onClose={closeModal}>
            <FormGrid>
              <GlassInput placeholder="Nome" value={equipamentoForm.nome} onChange={(e) => setEquipamentoForm({ ...equipamentoForm, nome: e.target.value })} />
              <GlassInput placeholder="Codigo" value={equipamentoForm.codigo} onChange={(e) => setEquipamentoForm({ ...equipamentoForm, codigo: e.target.value })} />
              <GlassInput placeholder="Categoria" value={equipamentoForm.categoria} onChange={(e) => setEquipamentoForm({ ...equipamentoForm, categoria: e.target.value })} />
              <GlassInput placeholder="Patrimonio" value={equipamentoForm.patrimonio} onChange={(e) => setEquipamentoForm({ ...equipamentoForm, patrimonio: e.target.value })} />
              <GlassInput placeholder="Marca" value={equipamentoForm.marca} onChange={(e) => setEquipamentoForm({ ...equipamentoForm, marca: e.target.value })} />
              <GlassInput placeholder="Modelo" value={equipamentoForm.modelo} onChange={(e) => setEquipamentoForm({ ...equipamentoForm, modelo: e.target.value })} />
              <GlassInput placeholder="Numero de serie" value={equipamentoForm.numero_serie} onChange={(e) => setEquipamentoForm({ ...equipamentoForm, numero_serie: e.target.value })} />
              <GlassSelect value={equipamentoForm.status} onChange={(e) => setEquipamentoForm({ ...equipamentoForm, status: e.target.value })}>
                <option value="disponivel">Disponivel</option>
                <option value="em_uso">Em uso</option>
                <option value="manutencao">Manutencao</option>
                <option value="baixado">Baixado</option>
              </GlassSelect>
              <GlassSelect value={equipamentoForm.condicao} onChange={(e) => setEquipamentoForm({ ...equipamentoForm, condicao: e.target.value })}>
                <option value="bom">Bom</option>
                <option value="regular">Regular</option>
                <option value="ruim">Ruim</option>
              </GlassSelect>
              <GlassInput placeholder="Localizacao" value={equipamentoForm.localizacao} onChange={(e) => setEquipamentoForm({ ...equipamentoForm, localizacao: e.target.value })} />
              <GlassSelect value={equipamentoForm.obra_id} onChange={(e) => setEquipamentoForm({ ...equipamentoForm, obra_id: e.target.value })}>
                <option value="">Obra atual</option>
                {obraOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
              </GlassSelect>
              <GlassInput placeholder="Responsavel" value={equipamentoForm.responsavel_nome} onChange={(e) => setEquipamentoForm({ ...equipamentoForm, responsavel_nome: e.target.value })} />
              <GlassInput type="date" placeholder="Aquisicao" value={equipamentoForm.data_aquisicao} onChange={(e) => setEquipamentoForm({ ...equipamentoForm, data_aquisicao: e.target.value })} />
              <GlassInput placeholder="Valor aquisicao" value={equipamentoForm.valor_aquisicao} onChange={(e) => setEquipamentoForm({ ...equipamentoForm, valor_aquisicao: e.target.value })} />
              <GlassInput type="date" placeholder="Garantia" value={equipamentoForm.data_garantia} onChange={(e) => setEquipamentoForm({ ...equipamentoForm, data_garantia: e.target.value })} />
              <GlassInput type="date" placeholder="Proxima manutencao" value={equipamentoForm.proxima_manutencao} onChange={(e) => setEquipamentoForm({ ...equipamentoForm, proxima_manutencao: e.target.value })} />
            </FormGrid>
            <GlassTextarea className="mt-3" placeholder="Observacoes" value={equipamentoForm.observacoes} onChange={(e) => setEquipamentoForm({ ...equipamentoForm, observacoes: e.target.value })} />
            <ModalActions onCancel={closeModal} onSave={saveEquipamento} saving={saving} />
          </Modal>
        )}

        {modal === 'equipMov' && (
          <Modal title="Movimentar equipamento" onClose={closeModal}>
            <FormGrid>
              <GlassSelect value={selectedEquipamentoId} onChange={(e) => setSelectedEquipamentoId(e.target.value)}>
                <option value="">Equipamento</option>
                {equipamentoOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
              </GlassSelect>
              <GlassSelect value={equipMovForm.tipo} onChange={(e) => setEquipMovForm({ ...equipMovForm, tipo: e.target.value })}>
                <option value="alocacao">Alocacao</option>
                <option value="devolucao">Devolucao</option>
                <option value="manutencao">Manutencao</option>
                <option value="ajuste">Ajuste</option>
                <option value="baixa">Baixa</option>
              </GlassSelect>
              <GlassInput type="date" value={equipMovForm.data} onChange={(e) => setEquipMovForm({ ...equipMovForm, data: e.target.value })} />
              <GlassSelect value={equipMovForm.obra_id} onChange={(e) => setEquipMovForm({ ...equipMovForm, obra_id: e.target.value })}>
                <option value="">Obra</option>
                {obraOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
              </GlassSelect>
              <GlassInput placeholder="Localizacao" value={equipMovForm.localizacao} onChange={(e) => setEquipMovForm({ ...equipMovForm, localizacao: e.target.value })} />
              <GlassInput placeholder="Responsavel" value={equipMovForm.responsavel_nome} onChange={(e) => setEquipMovForm({ ...equipMovForm, responsavel_nome: e.target.value })} />
              <GlassSelect value={equipMovForm.condicao} onChange={(e) => setEquipMovForm({ ...equipMovForm, condicao: e.target.value })}>
                <option value="bom">Bom</option>
                <option value="regular">Regular</option>
                <option value="ruim">Ruim</option>
              </GlassSelect>
              <GlassInput placeholder="Custo" value={equipMovForm.custo} onChange={(e) => setEquipMovForm({ ...equipMovForm, custo: e.target.value })} />
              <GlassInput type="date" placeholder="Proxima manutencao" value={equipMovForm.proxima_manutencao} onChange={(e) => setEquipMovForm({ ...equipMovForm, proxima_manutencao: e.target.value })} />
            </FormGrid>
            <GlassTextarea className="mt-3" placeholder="Observacao" value={equipMovForm.observacao} onChange={(e) => setEquipMovForm({ ...equipMovForm, observacao: e.target.value })} />
            <ModalActions onCancel={closeModal} onSave={saveEquipMov} saving={saving} />
          </Modal>
        )}

        {modal === 'movimento' && (
          <Modal title={movimentoForm.tipo === 'saida' ? 'Saida/consumo de material' : 'Entrada/ajuste de material'} onClose={closeModal}>
            <FormGrid>
              <GlassSelect value={movimentoForm.material_id} onChange={(e) => {
                const material = materiais.find((item) => item.id === e.target.value);
                setMovimentoForm({ ...movimentoForm, material_id: e.target.value, valor_unitario: material?.custo_unitario || '' });
              }}>
                <option value="">Material</option>
                {materialOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
              </GlassSelect>
              <GlassSelect value={movimentoForm.tipo} onChange={(e) => setMovimentoForm({ ...movimentoForm, tipo: e.target.value, motivo: e.target.value === 'saida' ? 'consumo' : 'ajuste' })}>
                <option value="entrada">Entrada</option>
                <option value="saida">Saida</option>
              </GlassSelect>
              <GlassInput placeholder="Quantidade" value={movimentoForm.quantidade} onChange={(e) => setMovimentoForm({ ...movimentoForm, quantidade: e.target.value })} />
              <GlassInput placeholder="Valor unitario" value={movimentoForm.valor_unitario} onChange={(e) => setMovimentoForm({ ...movimentoForm, valor_unitario: e.target.value })} />
              <GlassSelect value={movimentoForm.obra_id} onChange={async (e) => {
                setMovimentoForm({ ...movimentoForm, obra_id: e.target.value, etapa_id: '' });
                await loadEtapas(e.target.value);
              }}>
                <option value="">Obra</option>
                {obraOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
              </GlassSelect>
              <GlassSelect value={movimentoForm.etapa_id} onChange={(e) => setMovimentoForm({ ...movimentoForm, etapa_id: e.target.value })}>
                <option value="">Etapa</option>
                {etapas.map((item) => <option key={item.id} value={item.id}>{item.nome}</option>)}
              </GlassSelect>
              <GlassSelect value={movimentoForm.fornecedor_id} onChange={(e) => setMovimentoForm({ ...movimentoForm, fornecedor_id: e.target.value })}>
                <option value="">Fornecedor</option>
                {fornecedorOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
              </GlassSelect>
              <GlassInput placeholder="Documento/NF" value={movimentoForm.documento} onChange={(e) => setMovimentoForm({ ...movimentoForm, documento: e.target.value })} />
            </FormGrid>
            <GlassTextarea className="mt-3" placeholder="Observacoes" value={movimentoForm.observacoes} onChange={(e) => setMovimentoForm({ ...movimentoForm, observacoes: e.target.value })} />
            <ModalActions onCancel={closeModal} onSave={saveMovimento} saving={saving} />
          </Modal>
        )}
      </div>
    </FadeIn>
  );
};

const Info = ({ label, value }) => (
  <div className="rounded-lg bg-white/5 p-3">
    <p className="text-xs text-white/40">{label}</p>
    <p className="text-sm font-semibold text-white mt-1">{value}</p>
  </div>
);

const TableHeader = ({ cols }) => (
  <div className="grid grid-cols-6 gap-4 px-5 py-3 text-xs uppercase tracking-wide text-white/40">
    {cols.map((col) => <span key={col}>{col}</span>)}
  </div>
);

const Empty = ({ text }) => <div className="px-5 py-8 text-center text-sm text-white/50 border-t border-white/10">{text}</div>;

const Modal = ({ title, children, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
    <GlassCard className="w-full max-w-3xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-white">{title}</h2>
        <button className="text-white/50 hover:text-white" onClick={onClose}>fechar</button>
      </div>
      {children}
    </GlassCard>
  </div>
);

const FormGrid = ({ children }) => <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>;

const ModalActions = ({ onCancel, onSave, saving }) => (
  <div className="flex justify-end gap-2 mt-5">
    <GlassButton variant="secondary" onClick={onCancel}>Cancelar</GlassButton>
    <GlassButton onClick={onSave} disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</GlassButton>
  </div>
);

export default Estoque;
